import React, { createContext, useContext, useState, useEffect } from 'react';

export type Language = 'en' | 'ar';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations: Record<Language, Record<string, string>> = {
  en: {
    // Header
    'app.title': 'Prayer Times',
    'app.subtitle': 'Windsor, Ontario',

    // Prayer names
    'prayer.fajr': 'Fajr',
    'prayer.sunrise': 'Sunrise',
    'prayer.dhuhr': 'Dhuhr',
    'prayer.asr': 'Asr',
    'prayer.maghrib': 'Maghrib',
    'prayer.isha': 'Isha',

    // Status
    'status.now': 'Now',
    'status.next': 'Next',
    'status.passed': 'Passed',
    'status.upcoming': 'Upcoming',

    // Time
    'time.until': 'Until',
    'time.in': 'in',
    'time.hours': 'hours',
    'time.minutes': 'minutes',
    'time.seconds': 'seconds',

    // Layout
    'layout.single': 'Single Prayer',
    'layout.all': 'All Prayers',
    'layout.table': 'Table View',

    // Install
    'install.title': 'Install Prayer Times',
    'install.description': 'Install this app on your device for quick access to prayer times',
    'install.button': 'Install',
    'install.later': 'Maybe Later',

    // Notifications
    'notification.title': 'Enable Notifications',
    'notification.description': 'Get notified when it\'s time for prayer',
    'notification.enable': 'Enable Notifications',
    'notification.dismiss': 'Not Now',

    // Share
    'share.title': 'Subscribe Someone to Prayer Notifications',
    'share.subtitle': 'Gift them daily prayer time reminders with Quranic verses',
    'share.name': 'Name:',
    'share.email': 'Email Address:',
    'share.city': 'City:',
    'share.country': 'Country:',
    'share.button': 'Subscribe Them Now',
    'share.receive': 'What they\'ll receive:',
    'share.feature1': 'Daily prayer-time notifications for their location',
    'share.feature2': 'Quranic verses and authentic Hadiths',
    'share.feature3': 'Islamic events and special occasions',
    'share.success': 'Successfully Subscribed!',
    'share.successReward': 'جَزَاكَ اللهُ خَيْرًا • May Allah reward you',
    'share.error': 'Failed to subscribe. Please try again.',
    'share.calculating': 'Prayer times will be calculated based on their location',
    'share.tooltip': 'Add Email Subscription',
    'share.subscribing': 'Subscribing...',
    'share.placeholderName': 'John Doe',
    'share.placeholderEmail': 'john@example.com',
    'share.placeholderCity': 'Windsor',
    'share.placeholderCountry': 'Canada',

    // Subscribe page
    'subscribe.title': 'Subscribe to Prayer Notifications',
    'subscribe.subtitle': 'Get daily reminders for prayer times with Quranic verses',
    'subscribe.button': 'Subscribe Now',
    'subscribe.success': 'Successfully subscribed! You will receive prayer notifications daily.',

    // Kiosk
    'kiosk.enter': 'Enter Kiosk Mode',
    'kiosk.exit': 'Exit Kiosk Mode',

    // Common
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.close': 'Close',

    // Dates
    'date.today': 'Today',
    'date.hijri': 'Hijri'
  },
  ar: {
    // Header
    'app.title': 'مواقيت الصلاة',
    'app.subtitle': 'ويندسور، أونتاريو',

    // Prayer names
    'prayer.fajr': 'الفجر',
    'prayer.sunrise': 'الشروق',
    'prayer.dhuhr': 'الظهر',
    'prayer.asr': 'العصر',
    'prayer.maghrib': 'المغرب',
    'prayer.isha': 'العشاء',

    // Status
    'status.now': 'الآن',
    'status.next': 'التالية',
    'status.passed': 'مضت',
    'status.upcoming': 'قادمة',

    // Time
    'time.until': 'حتى',
    'time.in': 'في',
    'time.hours': 'ساعات',
    'time.minutes': 'دقائق',
    'time.seconds': 'ثواني',

    // Layout
    'layout.single': 'صلاة واحدة',
    'layout.all': 'جميع الصلوات',
    'layout.table': 'عرض الجدول',

    // Install
    'install.title': 'تثبيت تطبيق مواقيت الصلاة',
    'install.description': 'ثبت هذا التطبيق على جهازك للوصول السريع إلى مواقيت الصلاة',
    'install.button': 'تثبيت',
    'install.later': 'ربما لاحقاً',

    // Notifications
    'notification.title': 'تفعيل الإشعارات',
    'notification.description': 'احصل على تنبيه عند حلول وقت الصلاة',
    'notification.enable': 'تفعيل الإشعارات',
    'notification.dismiss': 'ليس الآن',

    // Share
    'share.title': 'اشترك شخصاً في إشعارات الصلاة',
    'share.subtitle': 'اهدهم تذكيرات يومية لأوقات الصلاة مع آيات قرآنية',
    'share.name': 'الاسم:',
    'share.email': 'البريد الإلكتروني:',
    'share.city': 'المدينة:',
    'share.country': 'الدولة:',
    'share.button': 'اشترك الآن',
    'share.receive': 'ما سيحصلون عليه:',
    'share.feature1': 'إشعارات يومية لأوقات الصلاة حسب موقعهم',
    'share.feature2': 'آيات قرآنية وأحاديث صحيحة',
    'share.feature3': 'مناسبات إسلامية خاصة',
    'share.success': 'تم الاشتراك بنجاح!',
    'share.successReward': 'جَزَاكَ اللهُ خَيْرًا',
    'share.error': 'فشل الاشتراك. يرجى المحاولة مرة أخرى.',
    'share.calculating': 'سيتم حساب أوقات الصلاة بناءً على موقعهم',
    'share.tooltip': 'إضافة اشتراك بريد إلكتروني',
    'share.subscribing': 'جاري الاشتراك...',
    'share.placeholderName': 'محمد أحمد',
    'share.placeholderEmail': 'email@example.com',
    'share.placeholderCity': 'ويندسور',
    'share.placeholderCountry': 'كندا',

    // Subscribe page
    'subscribe.title': 'اشترك في إشعارات الصلاة',
    'subscribe.subtitle': 'احصل على تذكيرات يومية لأوقات الصلاة مع آيات قرآنية',
    'subscribe.button': 'اشترك الآن',
    'subscribe.success': 'تم الاشتراك بنجاح! ستتلقى إشعارات الصلاة يومياً.',

    // Kiosk
    'kiosk.enter': 'الدخول لوضع الكشك',
    'kiosk.exit': 'الخروج من وضع الكشك',

    // Common
    'common.loading': 'جاري التحميل...',
    'common.error': 'خطأ',
    'common.close': 'إغلاق',

    // Dates
    'date.today': 'اليوم',
    'date.hijri': 'هجري'
  }
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('language');
    return (saved === 'ar' || saved === 'en') ? saved : 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
